import React, {useCallback, useEffect, useState} from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, IconButton, Collapse, Box, Pagination, TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserProfileData } from '../../services/Types';
import UserDetailsPanel from "./UserDetailsPanel";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {deleteUser, fetchAllUsers} from "../../services/HttpService";
import {useAuth} from "../../services/AuthProvider";
import {useNavigate} from "react-router-dom";
import ConfirmModal from "../../components/Modal/ConfirmCancelPanel";
import {AlertColor} from "@mui/material/Alert/Alert";
import Button from "@mui/material/Button";
import SnackbarAlertMessage from "../../components/Modal/SnackbarAlertMessage";

const UserRow: React.FC<{
  user: UserProfileData,
  onDelete: (uuid: string) => Promise<void>,
  handleShowAlertMessage: (severity: AlertColor, message: string) => void,
  isExpanded: boolean,
  onToggle: () => void
}> = ({ user, onDelete, handleShowAlertMessage, isExpanded, onToggle }) => {
  const [isDeleteUserConfirmModalOpen, setDeleteUserConfirmModalOpen] = useState(false);

  const handleDeleteUser = async () => {
    try {
      setDeleteUserConfirmModalOpen(false);
      await onDelete(user.uuid!);
      handleShowAlertMessage('success', 'User deleted successfully!');
    } catch (error: any) {
      handleShowAlertMessage('error', 'Failed to delete the user. ' + error.message);
    }
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{user.username}</TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell>{user.createdAt ? new Date(user.createdAt * 1000).toLocaleString() : '-'}</TableCell>
        <TableCell>{user.accessedAt ? new Date(user.accessedAt * 1000).toLocaleString() : '-'}</TableCell>
        <TableCell>
          <ConfirmModal
            isOpen={isDeleteUserConfirmModalOpen}
            onClose={() => setDeleteUserConfirmModalOpen(false)}
            onConfirm={handleDeleteUser}
            title="Are you sure you want to delete this user account?"
            message={"Deleting user account will permanently remove all the data of this user from our system. This action cannot be undone."}
          />
          <IconButton onClick={() => setDeleteUserConfirmModalOpen(true)}>
            <DeleteIcon color="error" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <UserDetailsPanel userDetails={user} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const UserTable: React.FC<{ users: UserProfileData[], onDelete: (uuid: string) => Promise<void>, handleShowAlertMessage: (severity: AlertColor, message: string) => void, }> = ({ users, onDelete, handleShowAlertMessage }) => {
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string | null>(null);

  // Handle sorting
  const handleSort = (keyText: string) => {
    const property = keyText.replace(/\s+/g, '_').toLowerCase();
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Function to handle different types of values including dates
  const normalizeSortValue = (value: any): string | number => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.toLowerCase();
    if (value instanceof Date) return value.getTime();
    return value;
  }

  const sortedUsers = React.useMemo(() => {
    if (!orderBy) return users;
    return [...users].sort((a, b) => {
      let valueA = normalizeSortValue(a[orderBy as keyof UserProfileData]);
      let valueB = normalizeSortValue(b[orderBy as keyof UserProfileData]);

      if (valueA < valueB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [users, order, orderBy]);

  // expand
  const handleToggle = (userId: string) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              {['Username', 'Email', 'Created At', 'Accessed At'].map((text) => (
                <TableCell key={text}>
                  <TableSortLabel
                    active={orderBy === text.replace(/\s+/g, '_').toLowerCase()}
                    direction={orderBy === text.replace(/\s+/g, '_').toLowerCase() ? order : 'asc'}
                    onClick={() => handleSort(text)}
                  >
                    {text}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <UserRow
                key={user.uuid}
                user={user}
                onDelete={onDelete}
                handleShowAlertMessage={handleShowAlertMessage}
                isExpanded={expandedUserId === user.uuid}
                onToggle={() => handleToggle(user.uuid!)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>

  );
};

const AdminTable: React.FC = () => {
  const { isLoggedIn, userProfile } = useAuth();
  const navigate = useNavigate(); // Initialize useNavigate

  const [users, setUsers] = useState<UserProfileData[]>([]);
  const [totalUserSize, setTotalUserSize] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [emailFilter, setEmailFilter] = useState("");
  const [usernameFilter, setUsernameFilter] = useState("");
  const [createdAtStartTimeFilter, setCreatedAtStartTimeFilter] = useState(0);
  const [createdAtEndTimeFilter, setCreatedAtEndTimeFilter] = useState(0);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderType, setOrderType] = useState("ASC");

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openAlertMessage, setOpenAlertMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('info' as AlertColor);

  const handleShowAlertMessage = (newSeverity: AlertColor, newMessage: string) => {
    setSeverity(newSeverity);
    setMessage(newMessage);
    setOpenAlertMessage(true);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const result = await fetchAllUsers(currentPage, itemsPerPage, emailFilter, usernameFilter, createdAtStartTimeFilter, createdAtEndTimeFilter, orderBy, orderType);
      setTotalUserSize(result.total);
      setUsers(result.users);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, emailFilter, usernameFilter, createdAtStartTimeFilter, createdAtEndTimeFilter, orderBy, orderType]);

  useEffect(() => {
    if (!isLoggedIn || !userProfile || userProfile.role !== 'admin') {
      navigate('/');
    }

    fetchUsers();
  }, [isLoggedIn, userProfile, navigate, fetchUsers]);

  const handleSearch = async () => {
    try {
      setCurrentPage(1);
      const result = await fetchAllUsers(currentPage, itemsPerPage, emailFilter, usernameFilter, createdAtStartTimeFilter, createdAtEndTimeFilter, orderBy, orderType);
      setTotalUserSize(result.total);
      setUsers(result.users);
    } catch (error: any) {
      console.error('Failed to filter users:', error);
      setError(`Failed to filter users: ${error.message}`);
    }
  };

  const handleChangePage = async (event: React.ChangeEvent<unknown>, newPage: number) => {
    const result = await fetchAllUsers(newPage, itemsPerPage, emailFilter, usernameFilter, createdAtStartTimeFilter, createdAtEndTimeFilter, orderBy, orderType);
    setTotalUserSize(result.total);
    setUsers(result.users);
    setCurrentPage(newPage);
  };

  const handleDelete = async (uuid: string) => {
    try {
      await deleteUser(uuid);
      setUsers(prevUsers => prevUsers.filter(user => user.uuid !== uuid));
    } catch (error) {
      setError('Failed to delete user');
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  return (
    <div style={{background: "#04193B"}}>
      <SnackbarAlertMessage
        open={openAlertMessage}
        severity={severity}
        message={message}
        onClose={() => setOpenAlertMessage(false)}
      />
      <div style={{padding: '80px 0 20px 0'}}></div>
      <div style={{padding: "20px", background: "#fff"}}>
        <h1>SPARX Game Users</h1>
        {
          (isLoading || error) ? <div>{ isLoading ? 'Loading...': `Error: ${error}` }</div> :
            <div>
              <form onSubmit={(e) => {e.preventDefault(); handleSearch();}} style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
                {/* Email Filter */}
                <TextField
                  id="email-filter-input"
                  label="Search by email"
                  variant="outlined"
                  type="text"
                  placeholder="Input full email address"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  sx={{ width: '300px' }}
                />

                {/* Username Filter */}
                <TextField
                  id="username-filter-input"
                  label="Search by username"
                  variant="outlined"
                  type="text"
                  placeholder="Input username"
                  value={usernameFilter}
                  onChange={(e) => setUsernameFilter(e.target.value)}
                  sx={{ width: '300px' }}
                />

                {/* Created At Start Date Filter */}
                <TextField
                  id="created-at-start-filter"
                  label="Created At Start"
                  type="date"
                  variant="outlined"
                  value={createdAtStartTimeFilter ? new Date(createdAtStartTimeFilter).toISOString().split('T')[0] : ''}
                  onChange={(e) => setCreatedAtStartTimeFilter(new Date(e.target.value).getTime())}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: '200px' }}
                />

                {/* Created At End Date Filter */}
                <TextField
                  id="created-at-end-filter"
                  label="Created At End"
                  type="date"
                  variant="outlined"
                  value={createdAtEndTimeFilter ? new Date(createdAtEndTimeFilter).toISOString().split('T')[0] : ''}
                  onChange={(e) => setCreatedAtEndTimeFilter(new Date(e.target.value).getTime())}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: '200px' }}
                />

                {/* Sort By */}
                <FormControl variant="outlined" sx={{ minWidth: '200px' }}>
                  <InputLabel id="sort-by-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-by-label"
                    id="sort-by"
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="username">Username</MenuItem>
                    <MenuItem value="created_at">Created At</MenuItem>
                  </Select>
                </FormControl>

                {/* Order Type */}
                <FormControl variant="outlined" sx={{ minWidth: '200px' }}>
                  <InputLabel id="order-type-label">Order Type</InputLabel>
                  <Select
                    labelId="order-type-label"
                    id="order-type"
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                    label="Order Type"
                  >
                    <MenuItem value="ASC">Ascending</MenuItem>
                    <MenuItem value="DESC">Descending</MenuItem>
                  </Select>
                </FormControl>

                {/* Search Button */}
                <Button type="submit" variant="contained" sx={{ padding: '12px 30px' }}>Search</Button>
              </form>
              <br/>
              <UserTable users={users} onDelete={handleDelete} handleShowAlertMessage={handleShowAlertMessage}/>
              <br/>
              <Pagination
                count={Math.ceil(totalUserSize / itemsPerPage)}
                page={currentPage}
                onChange={handleChangePage}
              />
            </div>
        }
      </div>
    </div>
  );
};

export default AdminTable;
